<template>
  <div>
    <page-header
      title="租户管理"
      :crumbs="['系统设置', '租户管理']"
      :subtitle="`共 ${pagination.total} 个租户`"
    >
      <template #actions>
        <button v-if="hasPermission('sys:tenant:add')" class="btn btn-primary" @click="handleAdd">
          新增租户
        </button>
      </template>
    </page-header>

    <DataTable
      :columns="columns"
      :data="tenantList"
      :loading="tableLoading"
      :total="pagination.total"
      :page="pagination.currentPage"
      :page-size="pagination.pageSize"
      row-key="id"
      :selectable="hasPermission('sys:tenant:batchOperate')"
      empty-text="没有匹配的租户"
      @update:page="handleCurrentChange"
      @update:page-size="handleSizeChange"
      @refresh="getTenantList"
    >
      <template #filters>
        <div class="filter-bar">
          <div class="filter-inline">
            <label>关键词</label>
            <el-input
              v-model="searchKeyword"
              placeholder="租户名称或编码"
              clearable
              style="width: 240px"
              @keyup.enter="handleSearch"
            />
          </div>
          <button class="btn" @click="handleReset">重置</button>
          <button class="btn btn-primary" @click="handleSearch">查询</button>
        </div>
      </template>

      <template #cell-status="{ row }">
        <span class="tag" :class="row.status === 1 ? 'tag-success' : 'tag-neutral'">
          {{ row.status === 1 ? '正常' : '禁用' }}
        </span>
      </template>

      <template #cell-createTime="{ row }">
        {{ formatDate(row.createTime) || '—' }}
      </template>

      <!-- 说明：后端 TenantController 目前仅提供 create / getTenantList，
           未提供更新与删除接口，因此不开放编辑与删除入口。 -->
      <template #ops="{ row }">
        <span class="dt-hint">{{ row.tenantCode || '—' }}</span>
      </template>
    </DataTable>

    <!-- 新增租户 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="520px" :close-on-click-modal="false">
      <el-form ref="formRef" :model="formData" :rules="formRules" label-position="top">
        <el-form-item label="租户名称" prop="tenantName">
          <el-input v-model="formData.tenantName" placeholder="请输入租户名称" maxlength="100" />
        </el-form-item>
        <el-form-item label="租户编码" prop="tenantCode">
          <el-input v-model="formData.tenantCode" placeholder="请输入租户编码" maxlength="50" />
        </el-form-item>
        <div class="form-row">
          <el-form-item label="联系人" prop="contactPerson">
            <el-input v-model="formData.contactPerson" placeholder="请输入联系人" maxlength="50" />
          </el-form-item>
          <el-form-item label="联系电话" prop="contactPhone">
            <el-input v-model="formData.contactPhone" placeholder="请输入联系电话" maxlength="50" />
          </el-form-item>
        </div>
        <el-form-item label="联系邮箱">
          <el-input v-model="formData.contactEmail" placeholder="请输入联系邮箱" maxlength="100" />
        </el-form-item>
        <p class="form-hint">创建后将自动生成管理员账号 admin_{租户编码}，初始密码 123456</p>
      </el-form>
      <template #footer>
        <button class="btn" @click="dialogVisible = false">取消</button>
        <button class="btn btn-primary" @click="handleSubmit">确定</button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import PageHeader from '@/components/PageHeader.vue'
import DataTable from '@/components/DataTable.vue'
import tenantService from '@/services/tenantService'
import store from '@/store'

const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleString('zh-CN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit'
  })
}

export default {
  name: 'TenantManagement',
  components: { PageHeader, DataTable },
  setup() {
    const tenantList = ref([])
    const tableLoading = ref(false)
    const searchKeyword = ref('')
    const pagination = reactive({ currentPage: 1, pageSize: 20, total: 0 })

    const dialogVisible = ref(false)
    const dialogTitle = ref('新增租户')
    const formRef = ref(null)
    const formData = reactive({
      id: '', tenantName: '', tenantCode: '',
      contactPerson: '', contactPhone: '', contactEmail: '', status: 1
    })

    const formRules = {
      tenantName: [{ required: true, message: '请输入租户名称', trigger: 'blur' }],
      tenantCode: [
        { required: true, message: '请输入租户编码', trigger: 'blur' },
        { pattern: /^[a-zA-Z0-9_]+$/, message: '租户编码只能包含字母、数字和下划线', trigger: 'blur' }
      ],
      contactPerson: [{ required: true, message: '请输入联系人', trigger: 'blur' }],
      contactPhone: [{ required: true, message: '请输入联系电话', trigger: 'blur' }]
    }

    const columns = [
      { prop: 'id', label: 'ID', width: 70 },
      { prop: 'tenantName', label: '租户名称', minWidth: 170 },
      { prop: 'tenantCode', label: '租户编码', minWidth: 140 },
      { prop: 'contactPerson', label: '联系人', width: 110 },
      { prop: 'contactPhone', label: '联系电话', minWidth: 140 },
      { prop: 'status', label: '状态', width: 90 },
      { prop: 'createTime', label: '创建时间', minWidth: 150 }
    ]

    const permissionCodes = computed(() => {
      const permissions = store.state.permissions
      return permissions ? Object.values(permissions).map(p => p.permissionCode) : []
    })

    const hasPermission = (code) => permissionCodes.value.includes(code)

    // 兼容后端两种返回结构：数组 或 { list, total }
    const getTenantList = async () => {
      try {
        tableLoading.value = true
        const response = await tenantService.getTenantList({
          pageNum: pagination.currentPage,
          pageSize: pagination.pageSize,
          keyword: searchKeyword.value
        })
        const data = response?.data
        tenantList.value = Array.isArray(data) ? data : (data?.list || [])
        pagination.total = Array.isArray(data) ? data.length : (data?.total || 0)
      } catch (error) {
        ElMessage.error('获取租户列表失败：' + (error?.message || ''))
      } finally {
        tableLoading.value = false
      }
    }

    const handleSearch = () => {
      pagination.currentPage = 1
      getTenantList()
    }

    const handleReset = () => {
      searchKeyword.value = ''
      handleSearch()
    }

    const handleAdd = () => {
      Object.assign(formData, {
        id: '', tenantName: '', tenantCode: '',
        contactPerson: '', contactPhone: '', contactEmail: '', status: 1
      })
      dialogTitle.value = '新增租户'
      dialogVisible.value = true
      nextTick(() => formRef.value?.clearValidate())
    }

    // 说明：后端暂未提供租户更新接口，此处仅走新增
    const handleSubmit = async () => {
      if (!formRef.value) return
      try {
        await formRef.value.validate()
        const submitData = {
          ...formData,
          adminInfo: {
            username: `admin_${formData.tenantCode}`,
            realName: formData.contactPerson,
            phone: formData.contactPhone,
            email: formData.contactEmail,
            password: '123456'
          }
        }
        await tenantService.createTenant(submitData)
        ElMessage.success('租户创建成功')
        dialogVisible.value = false
        getTenantList()
      } catch (error) {
        if (error?.message) ElMessage.error(error.message)
      }
    }

    const handleSizeChange = (size) => {
      pagination.pageSize = size
      pagination.currentPage = 1
      getTenantList()
    }

    const handleCurrentChange = (page) => {
      pagination.currentPage = page
      getTenantList()
    }

    onMounted(getTenantList)

    return {
      tenantList, tableLoading, searchKeyword, pagination, columns,
      dialogVisible, dialogTitle, formRef, formData, formRules,
      formatDate, hasPermission, handleSearch, handleReset, handleAdd,
      handleSubmit, handleSizeChange, handleCurrentChange, getTenantList
    }
  }
}
</script>

<style scoped>
.filter-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.filter-inline {
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-inline label {
  font-size: 12px;
  color: var(--text-secondary);
  white-space: nowrap;
}

.form-row {
  display: flex;
  gap: 12px;
}

.form-row > * {
  flex: 1;
  min-width: 0;
}

.form-hint {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--text-tertiary);
  line-height: 1.5;
}

.tag {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: var(--radius-tag);
  white-space: nowrap;
}

.tag-success { background: var(--success-bg); color: var(--success-text); }
.tag-neutral { background: var(--bg-page);    color: var(--text-secondary); }

.dt-hint {
  font-size: 12px;
  color: var(--text-tertiary);
}

.btn {
  padding: 6px 14px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  background: var(--bg-card);
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
}

.btn:hover {
  border-color: var(--brand-500);
  color: var(--brand-500);
}

.btn-primary {
  background: var(--brand-500);
  border-color: var(--brand-500);
  color: #fff;
}

.btn-primary:hover {
  background: var(--brand-600);
  border-color: var(--brand-600);
  color: #fff;
}
</style>
